import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import _ from 'lodash';
import helper from '../../../utilities/helper';
import {validateValue, validateFields} from '../../../utilities/validation/validator';
import {showAppLoader, hideAppLoader} from '../../Shared/Loader/LoaderSlice';
import ProviderManager from '../../../provider/providerManager';
import {getRecaptchaToken} from '../../../utilities/recaptcha/recaptcha';
import asyncActionStatus from '../../../constans/asyncActionStatus';
import leadAppearanceMode from '../../../constans/leadAppearanceMode';
import callbackDesktopProviderMode from '../../../constans/callbackDesktopProviderMode';
import callbackMobileProviderMode from '../../../constans/callbackMobileProviderMode';
import {sessionGetAndClear} from '../../../utilities/leadStorageAPI/sessionStorageAPI';

const initialState = {
  isServiceCenterActive: null,
  contentData: {},
  showError: false,
  showSuccess: false,
  showPopup: true,
  leadForm: {
    fullName: '',
    phone: '',
    isAgreementsCheckBoxActive: false,
    errors: {
      fullName: '',
      phone: '',
    },
    valid: '',
  },
  callbackIvrForm: {
    phone: '',
    errors: {
      phone: '',
    },
    valid: '',
  },
};

const initRecaptchaToken = createAsyncThunk('leadBlock/initRecaptchaToken', async (data, thunkAPI) => {
  try {
    const token = await getRecaptchaToken('leadBlock');
    return {
      token,
    };
  } catch (error) {}
});

const checkIsServiceCenterActive = createAsyncThunk('leadBlock/checkIsServiceCenterActive', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('checkIsServiceCenterActive', [data.blockId]);
    return {
      errorCode: 0,
      isServiceCenterActive: result.Body.isServiceCenterActive,
    };
  } catch (error) {
    return {
      errorCode: error.errorCode,
      isServiceCenterActive: false,
    };
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

const createIvrCallback = createAsyncThunk('leadBlock/createIvrCallback', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());

    const leadBlockState = thunkAPI.getState().leadBlock;
    const recaptchaToken = leadBlockState.recaptchaToken;
    const phone = leadBlockState?.callbackIvrForm?.phone;
    const subjectCode = leadBlockState?.contentData?.ivrSkill;

    let clonedForm = _.cloneDeep(leadBlockState?.callbackIvrForm);
    const validatedForm = validateFields(clonedForm);

    if (validatedForm.valid === false) {
      return {
        isValid: false,
        form: validatedForm,
      };
    }

    const result = await ProviderManager.getProviderData('createIvrCallback', [phone, subjectCode]);
    return {
      isSuccess: result?.Body?.isSuccess,
    };
  } catch (error) {
    return {
      isSuccess: false,
    };
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

const createLead = createAsyncThunk('leadBlock/createLead', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());

    const leadBlockState = thunkAPI.getState().leadBlock;
    const recaptchaToken = leadBlockState.recaptchaToken;
    const fullName = leadBlockState?.leadForm?.fullName;
    const phone = leadBlockState?.leadForm?.phone;
    const sfCampaignCode = leadBlockState.contentData?.sfCampaignCode;
    const sfCampaignName = leadBlockState.contentData?.sfCampaignName;
    const sfProductId = leadBlockState.contentData?.sfCProductId;
    const adSource = helper.getUrlParameter('source');
    const url = encodeURI(window.location.origin + window.location.pathname);
    const bannerId = helper.getUrlParameter('medium');
    const searchTerm = helper.getUrlParameter('term');
    let zipCode = '';
    const isAgreementsCheckBoxActive = leadBlockState?.leadForm?.isAgreementsCheckBoxActive;
    const description = '';

    let clonedForm = _.cloneDeep(leadBlockState?.leadForm);
    const validatedForm = validateFields(clonedForm);

    if (validatedForm.valid === false) {
      return {
        isValid: false,
        form: validatedForm,
      };
    }
    const result = await ProviderManager.getProviderData('createLead', [
      recaptchaToken,
      fullName,
      phone,
      sfCampaignCode,
      sfCampaignName,
      sfProductId,
      adSource,
      url,
      bannerId,
      searchTerm,
      isAgreementsCheckBoxActive,
      description,
    ]);
    if (result?.Body?.isSuccess) {
      const additionalParams = sessionGetAndClear();
      let fiberAddress;
      if (additionalParams?.from?.includes('fiber')) {
        const address = additionalParams?.address;
        fiberAddress = address.cityName + ' , ' + address.streetName + ' ' + address.home;
        zipCode = address.zipCode;
      }

      if (additionalParams?.from == 'fiberNewSale') {
        thunkAPI.dispatch(reportAnalytics({eventCategory: 'fiber_newsale_process', eventAction: 'visibility_14', eventLabel: 'screen_2'}));
        thunkAPI.dispatch(
          report({type: 'addressNotConnectedToFiber', fiberAddress: fiberAddress, leadForm: leadBlockState?.leadForm, zipCode: zipCode}),
        );
      }
    }

    return {
      response: result?.Body,
    };
  } catch (error) {
    return {
      isSuccess: false,
    };
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

const leadBlockSlice = createSlice({
  name: 'leadBlock',
  initialState,
  reducers: {
    setInitialContent: {
      reducer(state, action) {
        state.contentData = action.payload;
      },
      prepare(payload) {
        return {
          payload: {
            blockId: payload.contentLink.id,
            appearanceMode: leadAppearanceMode[payload.eLeadBlockAppearanceMode.value],
            callbackDesktopProvider: callbackDesktopProviderMode[payload.eLeadCallbackDesktopProvider.value],
            callbackMobileProvider: callbackMobileProviderMode[payload.eLeadCallbackMobileProvider.value],
            sfCampaignCode: payload.sfCampaignCode.value,
            sfCampaignName: payload.sfCampaignName.value,
            sfCProductId: payload.sfCProductId.value,
            ivrSkill: payload.ivrSkill.value,
            directCallPhone: payload.directCallPhone.value,
            failureText: payload.failureText.value,
            leadSendButton: payload.leadSendButton.value,
            leadFormTitle: payload.leadFormTitle.value,
            leadFormSubTitle: payload.leadFormSubTitle.value,
            leadFormSuccessTitle: payload.leadFormSuccessTitle.value,
            leadFormSuccessSubTitle: payload.leadFormSuccessSubTitle.value,
            callbackIvrFormTitle: payload.callbackIvrFormTitle.value,
            callbackIvrFormSubTitle: payload.callbackIvrFormSubTitle.value,
            callbackIvrFormSuccessTitle: payload.callbackIvrFormSuccessTitle.value,
            callbackIvrFormSuccessSubTitle: payload.callbackIvrFormSuccessSubTitle.value,
            callbackDialerFormTitle: payload.callbackDialerFormTitle.value,
            callbackDialerFormSubTitle: payload.callbackDialerFormSubTitle.value,
            isHorizontalMode: payload?.isHorizontalMode?.value,
          },
        };
      },
    },
    setShowPopup: (state, action) => {
      state.showPopup = action.payload;
    },
    // Lead
    setNameLead: {
      reducer(state, action) {
        state.leadForm.fullName = action.payload.value;
        state.leadForm.errors.fullName = action.payload.error;
        state.leadForm.valid = action.payload.valid;
      },
      prepare(payload) {
        const value = payload.value;
        const error = validateValue(null, payload.name, payload.value);
        const valid = !error;
        return {
          payload: {
            value,
            error,
            valid,
          },
        };
      },
    },
    setPhoneLead: {
      reducer(state, action) {
        state.leadForm.phone = action.payload.value;
        state.leadForm.errors.phone = action.payload.error;
        state.leadForm.valid = action.payload.valid;
      },
      prepare(payload) {
        const value = payload.value;
        const error = validateValue(null, payload.name, payload.value);
        const valid = !error;
        return {
          payload: {
            value,
            error,
            valid,
          },
        };
      },
    },
    // CallbackIvr
    setPhoneCallbackIvr: {
      reducer(state, action) {
        state.callbackIvrForm.phone = action.payload.value;
        state.callbackIvrForm.errors.phone = action.payload.error;
        state.callbackIvrForm.valid = action.payload.valid;
      },
      prepare(payload) {
        const value = payload.value;
        const error = validateValue(null, payload.name, payload.value);
        const valid = !error;
        return {
          payload: {
            value,
            error,
            valid,
          },
        };
      },
    },
    setAgreementsCheckBox: (state, action) => {
      state.leadForm.isAgreementsCheckBoxActive = action.payload;
    },
    reportAnalytics: (state, action) => {},
    report: (state, action) => {},
    resetLeadBlock: (state) => initialState,
  },
  extraReducers: {
    [checkIsServiceCenterActive.fulfilled]: (state, action) => {
      if (action.payload.errorCode === 0) {
        state.checkServiceCenterActiveStatus = asyncActionStatus.SUCCESS;
        state.isServiceCenterActive = action.payload.isServiceCenterActive;
      } else {
        state.checkServiceCenterActiveStatus = asyncActionStatus.FAILURE;
        state.isServiceCenterActive = false; // lead form is default
      }
    },
    [createIvrCallback.fulfilled]: (state, action) => {
      if (action.payload.isValid === false) {
        state.callbackIvrForm = action.payload.form;
        return;
      }

      if (action.payload.isSuccess) {
        state.createIvrCallbackStatus = asyncActionStatus.SUCCESS;
        state.showSuccess = true;
        state.showError = false;
      } else {
        state.createIvrCallbackStatus = asyncActionStatus.FAILURE;
        state.showError = true;
        state.showSuccess = false;
      }
    },
    [createLead.fulfilled]: (state, action) => {
      if (action.payload.isValid === false) {
        state.leadForm = action.payload.form;
        return;
      }

      if (action.payload?.response?.isSuccess) {
        state.createLeadStatus = asyncActionStatus.SUCCESS;
        state.showSuccess = true;
        state.showError = false;
      } else {
        state.createLeadStatus = asyncActionStatus.FAILURE;
        state.showError = true;
        state.showSuccess = false;
      }
    },
    [initRecaptchaToken.fulfilled]: (state, action) => {
      state.recaptchaToken = action.payload?.token;
    },
  },
});

export const {
  setInitialContent,
  setShowPopup,
  setNameLead,
  setPhoneLead,
  setIsValidLead,
  setIsSubmitLead,
  setPhoneCallbackIvr,
  setIsValidCallbackIvr,
  setIsSubmitCallbackIvr,
  resetLeadBlock,
  report,
  reportAnalytics,
  setAgreementsCheckBox,
} = leadBlockSlice.actions;

export {initRecaptchaToken, checkIsServiceCenterActive, createIvrCallback, createLead};

export default leadBlockSlice.reducer;
